import classNames from "classnames";
import {
  InputMask,
  InputMaskChangeEvent,
  InputMaskProps
} from "primereact/inputmask";
import React from "react";

import { getDataTestId } from "../../../utils/getDataTestId";
import { getFsExcludeClassName } from "../../../utils/getFsExcludeClassName";
import { WSInputBaseProps } from "../../types/forms";
import {
  detachFieldProps,
  getFieldAriaProps,
  WSField,
  WSFieldProps
} from "../WSField/WSField";
import {
  WSInputContainer,
  WSInputContainerProps
} from "../WSInputContainer/WSInputContainer";

export type WSInputMaskProps = WSInputBaseProps<string, InputMaskChangeEvent> &
  Omit<WSInputContainerProps, "align" | "size"> &
  WSFieldProps & {
    mask?: InputMaskProps["mask"];
    slotChar?: InputMaskProps["mask"];
    autoClear?: InputMaskProps["autoClear"];
    unmask?: InputMaskProps["unmask"];
    readOnly?: InputMaskProps["readOnly"];
    onComplete?: InputMaskProps["onComplete"];
  };

export const WSInputMask = React.forwardRef<InputMask, WSInputMaskProps>(
  (props, ref) => {
    const {
      name,
      status,
      className,
      icon,
      iconRight,
      inputAction,
      disabled,
      onChange,
      inputClassName,
      fsExclude,
      ...otherProps
    } = props;

    const { fieldProps, ...inputProps } = detachFieldProps(otherProps);

    const testIdBase = getDataTestId("inputMask", name, fieldProps.label);
    const ariaProps = getFieldAriaProps({
      name,
      label: fieldProps.label,
      helperText: fieldProps.helperText,
      required: fieldProps.required,
      status,
      message: fieldProps.message
    });

    return (
      <WSField {...fieldProps} name={name} status={status}>
        <WSInputContainer
          status={status}
          icon={icon}
          iconRight={iconRight}
          inputAction={inputAction}
          disabled={disabled}
          className={className}
          data-testid={testIdBase}
        >
          <InputMask
            ref={ref}
            name={name}
            disabled={disabled}
            onChange={(event) => {
              const value =
                event.value ||
                event.target.value ||
                (event.originalEvent?.target as HTMLInputElement)?.value;
              onChange(value, event);
            }}
            type="text"
            className={classNames(
              inputClassName,
              getFsExcludeClassName(fsExclude)
            )}
            data-testid={`${testIdBase}Input`}
            {...ariaProps}
            {...inputProps}
          />
        </WSInputContainer>
      </WSField>
    );
  }
);

WSInputMask.displayName = "WSInputMask";
